/**
 * FRODO (Fixed Route On Demand Optimisation) Service
 * 
 * Preprocessing optimization that identifies rides that can be served by fixed routes
 * and removes them from on-demand simulation before execution.
 */

import type { RideSchema } from '../../types/simulation';
import type { FixedRoute, FixedRouteStop } from '../../types/fixedRoutes';
import type {
  FrodoConfig,
  FrodoOffloadedRide,
  FrodoOffloadedRideMetadata,
  FrodoReport,
  FrodoRouteStats,
  FrodoTimingStats,
  FrodoTransferStats,
} from '../../types/frodo';
import { getDistanceMeters } from '../../components/map/utils/distance';
import { addMinutesToTime, compareTimes } from '../../utils/fixedRoutes/timetableUtils';
import { calculateRouteAssignmentTimes } from '../../utils/fixedRoutes/routeAssignmentGenerator';
import { calculateRouteLoopDuration } from '../../utils/fixedRoutes/vehicleCalculator';
import { SpatialIndex } from '../../utils/frodo/spatialIndex';

/**
 * Convert HH:mm time string to total minutes since midnight
 * (Helper function since timeToMinutes is not exported from timetableUtils)
 */
function timeToMinutes(time: string): number {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
}

/**
 * Convert minutes since midnight to HH:mm time string
 */
function minutesToTime(minutes: number): string {
  const hours = Math.floor(minutes / 60) % 24;
  const mins = minutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
}

/**
 * Parse ISO datetime string and extract time portion (HH:mm)
 */
function extractTimeFromISO(isoString: string): string {
  const date = new Date(isoString);
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
}

/**
 * Result of evaluating a stop pair combination
 */
interface StopPairEvaluation {
  routeId: string;
  routeName: string;
  transferRouteId?: string;
  transferRouteName?: string;
  transferStopId?: string;
  transferStopName?: string;
  originStop: FixedRouteStop;
  destStop: FixedRouteStop;
  originDistance: number;
  destDistance: number;
  waitTimeMinutes: number;
  transferWaitTimeMinutes?: number;
  routeArrivalTime: string; // ISO datetime
  routeTravelTimeMinutes: number;
  totalWalkingDistance: number;
  requiresTransfer: boolean;
  score: number; // Lower is better
}

/**
 * Candidate stops with distances for origin or destination
 */
interface CandidateStop {
  stop: FixedRouteStop;
  distance: number;
}

/**
 * Find candidate stops within maxDistance of a point
 * 
 * @param ride The ride to find stops for
 * @param spatialIndex Spatial index of stops
 * @param maxDistance Maximum distance in meters
 * @returns Object with candidate origin stops and destination stops
 */
export function findCandidateStops(
  ride: RideSchema,
  spatialIndex: SpatialIndex,
  maxDistance: number
): {
  originStops: CandidateStop[];
  destStops: CandidateStop[];
} {
  // Query ALL stops within maxDistance of origin
  const originCandidates = spatialIndex.queryWithinRadius(
    [ride.from_latitude, ride.from_longitude],
    maxDistance
  );

  // Calculate exact distance for each candidate origin stop
  const originStops: CandidateStop[] = originCandidates
    .map((stop) => ({
      stop,
      distance: getDistanceMeters(
        [ride.from_latitude, ride.from_longitude],
        [stop.coordinates.lat, stop.coordinates.lng]
      ),
    }))
    .filter((item) => item.distance <= maxDistance)
    .sort((a, b) => a.distance - b.distance);

  // Query ALL stops within maxDistance of destination
  const destCandidates = spatialIndex.queryWithinRadius(
    [ride.to_latitude, ride.to_longitude],
    maxDistance
  );

  // Calculate exact distance for each candidate destination stop
  const destStops: CandidateStop[] = destCandidates
    .map((stop) => ({
      stop,
      distance: getDistanceMeters(
        [ride.to_latitude, ride.to_longitude],
        [stop.coordinates.lat, stop.coordinates.lng]
      ),
    }))
    .filter((item) => item.distance <= maxDistance)
    .sort((a, b) => a.distance - b.distance);

  return { originStops, destStops };
}

/**
 * Find the next trip that arrives at a stop after the request time
 * Uses efficient search when checking multiple trips
 * 
 * @param tripSchedules Array of trip schedules (sorted by start time)
 * @param stopIndex Index of the stop in the route
 * @param requestMinutes Request time in minutes from midnight
 * @returns Trip schedule with arrival time, or null if no trip available
 */
function findNextTripForStop(
  tripSchedules: Array<{ startTimeMinutes: number; stopArrivalTimesMinutes: number[] }>,
  stopIndex: number,
  requestMinutes: number
): { trip: { startTimeMinutes: number; stopArrivalTimesMinutes: number[] }; arrivalMinutes: number } | null {
  if (tripSchedules.length === 0) return null;
  
  let bestTrip: { startTimeMinutes: number; stopArrivalTimesMinutes: number[] } | null = null;
  let bestArrivalMinutes: number | null = null;
  let minWaitTime = Infinity;

  // Linear scan with early exit optimization
  // For routes with many trips, could optimize with binary search, but linear is simpler and fast enough
  for (const trip of tripSchedules) {
    const relativeArrival = trip.stopArrivalTimesMinutes[stopIndex];
    if (relativeArrival < 0) continue; // Invalid arrival time for this stop

    // Calculate absolute arrival time
    let arrivalMinutes = trip.startTimeMinutes + relativeArrival;
    
    // Handle overnight trips (arrival might be next day)
    if (arrivalMinutes < trip.startTimeMinutes) {
      arrivalMinutes += 24 * 60;
    }

    // Normalize request time for comparison (handle overnight)
    let normalizedRequest = requestMinutes;
    if (normalizedRequest < trip.startTimeMinutes) {
      normalizedRequest += 24 * 60;
    }

    // Check if this trip arrives at or after request time
    if (arrivalMinutes >= normalizedRequest) {
      const waitTime = arrivalMinutes - normalizedRequest;
      if (waitTime < minWaitTime) {
        minWaitTime = waitTime;
        bestTrip = trip;
        bestArrivalMinutes = arrivalMinutes % (24 * 60); // Normalize to 0-1439
      }
    } else {
      // Trip arrives before request - check if it's the next day's first trip
      // (for routes that wrap around midnight)
      const nextDayArrival = arrivalMinutes + 24 * 60;
      if (nextDayArrival >= normalizedRequest) {
        const waitTime = nextDayArrival - normalizedRequest;
        if (waitTime < minWaitTime) {
          minWaitTime = waitTime;
          bestTrip = trip;
          bestArrivalMinutes = nextDayArrival % (24 * 60);
        }
      }
    }
  }

  if (bestTrip && bestArrivalMinutes !== null) {
    return { trip: bestTrip, arrivalMinutes: bestArrivalMinutes };
  }

  return null;
}

/**
 * Calculate when a route arrives at a specific stop based on request time
 * 
 * @param route The fixed route
 * @param stopId The stop ID to calculate arrival time for
 * @param requestTime ISO datetime string of the ride request time
 * @param stops Map of all stops (for stop names)
 * @returns ISO datetime string of when the route arrives at the stop, or null if not reachable
 */
export function calculateRouteArrivalTime(
  route: FixedRoute,
  stopId: string,
  requestTime: string,
  stops: Map<string, FixedRouteStop>
): string | null {
  // Find stop index in route
  const stopIndex = route.stops.findIndex((stop) => stop.stopId === stopId);
  if (stopIndex === -1) {
    console.warn(`FRODO: Stop ${stopId} not found on route ${route.id}`);
    return null; // Stop not found on route
  }
  
  // Validate route has required schedule information
  if (!route.startTime) {
    console.warn(`FRODO: Route ${route.id} missing startTime`);
    return null;
  }
  
  // Validate route has segments for travel time calculation
  if (!route.segments || route.segments.length === 0) {
    console.warn(`FRODO: Route ${route.id} has no segments, cannot calculate arrival time`);
    return null;
  }

  // Parse request time - FRODO is date-agnostic, only uses time portion
  const requestDate = new Date(requestTime);
  if (isNaN(requestDate.getTime())) {
    console.warn(`FRODO: Invalid request time format: ${requestTime}`);
    return null;
  }
  
  const requestTimeStr = extractTimeFromISO(requestTime);
  if (!requestTimeStr) {
    console.warn(`FRODO: Could not extract time from request: ${requestTime}`);
    return null;
  }
  
  const requestMinutes = timeToMinutes(requestTimeStr);

  // Get route start time - routes are date-agnostic, operating hours apply to any date
  const routeStartMinutes = timeToMinutes(route.startTime);
  const routeEndMinutes = route.endTime ? timeToMinutes(route.endTime) : 22 * 60;

  // Handle overnight routes
  const normalizedRouteEnd = routeEndMinutes < routeStartMinutes
    ? routeEndMinutes + 24 * 60
    : routeEndMinutes;
  const normalizedRequestMinutes = requestMinutes < routeStartMinutes
    ? requestMinutes + 24 * 60
    : requestMinutes;

  // Check if request time is within route operating hours (date-agnostic check)
  // Note: This ignores GTFS calendar dates - routes operate based on time only
  if (normalizedRequestMinutes < routeStartMinutes || normalizedRequestMinutes > normalizedRouteEnd) {
    console.debug(`FRODO: Request time ${requestTimeStr} outside route ${route.id} operating hours (${route.startTime}-${route.endTime || '22:00'})`);
    return null; // Route not operating at request time
  }

  const trafficMultiplier = route.trafficMultiplier || 1.0;

  // Calculate cumulative travel time from route start to target stop
  let cumulativeTime = 0;

  for (let i = 0; i < stopIndex; i++) {
    const currentStopId = route.stops[i].stopId;
    const nextStopId = route.stops[i + 1].stopId;

    // Find segment between current and next stop
    const segment = route.segments.find(
      (seg) => seg.fromStopId === currentStopId && seg.toStopId === nextStopId
    );

    if (segment) {
      cumulativeTime += Math.round(segment.travelTimeMinutes * trafficMultiplier);
    }

    // Add wait time at next stop
    cumulativeTime += route.stops[i + 1].waitTimeMinutes;
  }

  // Handle overnight routes (end time before start time)
  const isOvernight = compareTimes(route.startTime, route.endTime || '23:59') > 0;
  
  // For circulator routes, calculate which loop arrives closest to request time
  if (route.isCirculator) {
    const loopDuration = calculateRouteLoopDuration(route, trafficMultiplier);
    
    if (loopDuration <= 0) {
      console.warn(`FRODO: Invalid loop duration for route ${route.id}, skipping`);
      return null; // Invalid route configuration
    }
    
    // Handle overnight circulator routes
    if (isOvernight) {
      // For overnight routes, we need to handle the wrap-around case
      // This is complex - for now, log a warning and skip
      console.warn(`FRODO: Overnight circulator routes not fully supported for route ${route.id}`);
      // Could implement overnight logic here if needed
    }

    // Calculate time from route start to first arrival at target stop
    const firstArrivalMinutes = routeStartMinutes + cumulativeTime;

    // Incorporate circulator vehicle count (headway):
    // multiple vehicles are evenly phased around the loop.
    const vehicleCount = Math.max(1, Math.floor(route.vehicleCount || 1));
    const headwayMinutes = loopDuration / vehicleCount;

    let bestArrivalMinutes: number | null = null;
    for (let vehicleIndex = 0; vehicleIndex < vehicleCount; vehicleIndex++) {
      const phaseOffset = Math.round(vehicleIndex * headwayMinutes);
      const firstVehicleArrival = firstArrivalMinutes + phaseOffset;
      const loopsCompleted = Math.max(
        0,
        Math.floor((normalizedRequestMinutes - firstVehicleArrival) / loopDuration)
      );

      let candidateArrival = firstVehicleArrival + loopsCompleted * loopDuration;
      if (candidateArrival < normalizedRequestMinutes) {
        candidateArrival += loopDuration;
      }

      if (
        (bestArrivalMinutes === null || candidateArrival < bestArrivalMinutes) &&
        candidateArrival <= normalizedRouteEnd
      ) {
        bestArrivalMinutes = candidateArrival;
      }
    }

    if (bestArrivalMinutes === null) {
      return null;
    }

    // Normalize back to 24-hour format
    const normalizedArrival = bestArrivalMinutes % (24 * 60);

    // Create ISO datetime with arrival time
    const arrivalDate = new Date(requestDate);
    arrivalDate.setHours(Math.floor(normalizedArrival / 60));
    arrivalDate.setMinutes(normalizedArrival % 60);
    arrivalDate.setSeconds(0);
    arrivalDate.setMilliseconds(0);

    return arrivalDate.toISOString();
  } else {
    // Non-circulator route: check all trips if trip schedules are available
    if (route.tripSchedules && route.tripSchedules.length > 0) {
      // Use trip schedules to find the next available trip
      const nextTrip = findNextTripForStop(route.tripSchedules, stopIndex, normalizedRequestMinutes);
      
      if (nextTrip) {
        // Create ISO datetime with arrival time from trip schedule
        const arrivalDate = new Date(requestDate);
        arrivalDate.setHours(Math.floor(nextTrip.arrivalMinutes / 60));
        arrivalDate.setMinutes(nextTrip.arrivalMinutes % 60);
        arrivalDate.setSeconds(0);
        arrivalDate.setMilliseconds(0);
        return arrivalDate.toISOString();
      }
      
      // No trip found in schedules, fall back to single trip calculation
      // (This shouldn't happen if trip schedules are complete, but handle gracefully)
    }

    // Fallback when no trip schedules exist:
    // treat the configured route pattern as recurring through operating hours.
    const firstArrivalMinutes = routeStartMinutes + cumulativeTime;
    const routePatternMinutes = calculateRouteLoopDuration(route, trafficMultiplier);

    if (routePatternMinutes > 0) {
      const loopsSinceFirstArrival = Math.max(
        0,
        Math.ceil((normalizedRequestMinutes - firstArrivalMinutes) / routePatternMinutes)
      );
      const candidateArrivalMinutes = firstArrivalMinutes + loopsSinceFirstArrival * routePatternMinutes;

      if (candidateArrivalMinutes > normalizedRouteEnd) {
        return null; // No remaining service within operating window
      }

      const normalizedArrival = candidateArrivalMinutes % (24 * 60);
      const arrivalDate = new Date(requestDate);
      arrivalDate.setHours(Math.floor(normalizedArrival / 60));
      arrivalDate.setMinutes(normalizedArrival % 60);
      arrivalDate.setSeconds(0);
      arrivalDate.setMilliseconds(0);
      return arrivalDate.toISOString();
    }

    // Final fallback for malformed route patterns.
    const singleArrivalMinutes = routeStartMinutes + cumulativeTime;
    const normalizedArrival = singleArrivalMinutes % (24 * 60);
    const arrivalDate = new Date(requestDate);
    arrivalDate.setHours(Math.floor(normalizedArrival / 60));
    arrivalDate.setMinutes(normalizedArrival % 60);
    arrivalDate.setSeconds(0);
    arrivalDate.setMilliseconds(0);
    return arrivalDate.toISOString();
  }
}

/**
 * Calculate travel time from origin stop to destination stop on a route
 */
function calculateRouteTravelTime(
  route: FixedRoute,
  originStopId: string,
  destStopId: string
): number {
  // Validate route has segments
  if (!route.segments || route.segments.length === 0) {
    console.warn(`FRODO: Route ${route.id} has no segments, cannot calculate travel time`);
    return Infinity;
  }
  
  const originIndex = route.stops.findIndex((s) => s.stopId === originStopId);
  const destIndex = route.stops.findIndex((s) => s.stopId === destStopId);

  if (originIndex === -1 || destIndex === -1) {
    console.warn(`FRODO: Stop not found on route ${route.id}. Origin: ${originIndex === -1}, Dest: ${destIndex === -1}`);
    return Infinity; // Stop not found
  }
  
  if (originIndex === destIndex) {
    return Infinity; // Same stop is not a meaningful routed trip
  }
  if (!route.isCirculator && originIndex > destIndex) {
    return Infinity; // Invalid direction for non-circulator
  }

  const trafficMultiplier = route.trafficMultiplier || 1.0;
  let travelTime = 0;

  const stopCount = route.stops.length;
  const steps = route.isCirculator
    ? ((destIndex - originIndex + stopCount) % stopCount)
    : (destIndex - originIndex);

  if (steps <= 0) {
    return Infinity;
  }

  // Sum travel and intermediate wait across each leg on the chosen forward path.
  for (let step = 0; step < steps; step++) {
    const currentIndex = (originIndex + step) % stopCount;
    const nextIndex = (currentIndex + 1) % stopCount;
    const currentStopId = route.stops[currentIndex].stopId;
    const nextStopId = route.stops[nextIndex].stopId;

    const segment = route.segments.find(
      (seg) => seg.fromStopId === currentStopId && seg.toStopId === nextStopId
    );

    if (!segment) {
      return Infinity; // Incomplete route geometry/segments
    }

    travelTime += Math.round(segment.travelTimeMinutes * trafficMultiplier);

    // Add wait at intermediate stops only (not at destination stop)
    if (step < steps - 1) {
      travelTime += route.stops[nextIndex].waitTimeMinutes;
    }
  }

  return travelTime;
}

/**
 * Find routes containing a specific stop
 */
function findRoutesContainingStop(
  stopId: string,
  selectedRoutes: FixedRoute[]
): FixedRoute[] {
  return selectedRoutes.filter((route) =>
    route.stops.some((stop) => stop.stopId === stopId)
  );
}

/**
 * Find common stops between two route sets (transfer points)
 */
function findTransferPoints(
  routes1: FixedRoute[],
  routes2: FixedRoute[]
): Array<{ stopId: string; route1: FixedRoute; route2: FixedRoute }> {
  const transferPoints: Array<{ stopId: string; route1: FixedRoute; route2: FixedRoute }> = [];

  for (const route1 of routes1) {
    for (const route2 of routes2) {
      if (route1.id === route2.id) continue; // Same route, skip

      // Find common stops
      const route1StopIds = new Set(route1.stops.map((s) => s.stopId));
      for (const stop of route2.stops) {
        if (route1StopIds.has(stop.stopId)) {
          transferPoints.push({
            stopId: stop.stopId,
            route1,
            route2,
          });
        }
      }
    }
  }

  return transferPoints;
}

/**
 * Evaluate a single origin-destination stop pair combination
 * 
 * @param ride The ride to evaluate
 * @param originStop Origin stop candidate
 * @param destStop Destination stop candidate
 * @param originDistance Distance from ride origin to origin stop (meters)
 * @param destDistance Distance from ride destination to dest stop (meters)
 * @param selectedRoutes Routes to consider
 * @param config FRODO configuration
 * @param stops Map of all stops (for stop names and route calculations)
 * @returns Evaluation result with score, or null if no valid route found
 */
export function evaluateStopPair(
  ride: RideSchema,
  originStop: FixedRouteStop,
  destStop: FixedRouteStop,
  originDistance: number,
  destDistance: number,
  selectedRoutes: FixedRoute[],
  config: FrodoConfig,
  stops: Map<string, FixedRouteStop>
): StopPairEvaluation | null {
  if (!ride.request_time) {
    return null; // Need request time to calculate arrival
  }

  // Check all routes where both stops appear (not just the first match).
  const directRoutes = selectedRoutes.filter(
    (route) =>
      route.stops.some((s) => s.stopId === originStop.id) &&
      route.stops.some((s) => s.stopId === destStop.id)
  );

  let bestEvaluation: StopPairEvaluation | null = null;

  // Try each direct route and keep the best-scoring valid option.
  for (const directRoute of directRoutes) {
    const originIndex = directRoute.stops.findIndex((s) => s.stopId === originStop.id);
    const destIndex = directRoute.stops.findIndex((s) => s.stopId === destStop.id);
    const isValidDirection = directRoute.isCirculator
      ? originIndex !== destIndex
      : originIndex < destIndex;

    if (isValidDirection) {
      // Valid direction on route
      const routeArrivalTime = calculateRouteArrivalTime(
        directRoute,
        originStop.id,
        ride.request_time,
        stops
      );

      if (routeArrivalTime) {
        const arrivalDate = new Date(routeArrivalTime);
        const requestDate = new Date(ride.request_time);
        const waitTimeMinutes = (arrivalDate.getTime() - requestDate.getTime()) / (1000 * 60);

        // Check wait time constraint
        if (waitTimeMinutes >= 0 && waitTimeMinutes <= config.maxWaitTimeMinutes) {
          const routeTravelTime = calculateRouteTravelTime(
            directRoute,
            originStop.id,
            destStop.id
          );

          if (routeTravelTime < Infinity) {
            const totalWalkingDistance = originDistance + destDistance;

            // Score: prioritize walking distance, then route time, then wait time
            const score =
              totalWalkingDistance * 1000 + // Walking distance in meters (primary)
              routeTravelTime * 10 + // Route time in minutes (secondary)
              waitTimeMinutes; // Wait time in minutes (tertiary)

            const evaluation: StopPairEvaluation = {
              routeId: directRoute.id,
              routeName: directRoute.name,
              originStop,
              destStop,
              originDistance,
              destDistance,
              waitTimeMinutes,
              routeArrivalTime,
              routeTravelTimeMinutes: routeTravelTime,
              totalWalkingDistance,
              requiresTransfer: false,
              score,
            };

            if (!bestEvaluation || evaluation.score < bestEvaluation.score) {
              bestEvaluation = evaluation;
            }
          }
        }
      }
    }
  }

  // If no direct route and transfers allowed, try transfer routes
  if (!bestEvaluation && config.allowTransfers) {
    const originRoutes = findRoutesContainingStop(originStop.id, selectedRoutes);
    const destRoutes = findRoutesContainingStop(destStop.id, selectedRoutes);

    const transferPoints = findTransferPoints(originRoutes, destRoutes);

    for (const transfer of transferPoints) {
      const route1 = transfer.route1;
      const route2 = transfer.route2;

      // Calculate arrival at origin stop on route1
      const route1Arrival = calculateRouteArrivalTime(
        route1,
        originStop.id,
        ride.request_time,
        stops
      );

      if (!route1Arrival) continue;

      // Calculate travel time from origin stop to transfer stop on route1
      const route1TravelTime = calculateRouteTravelTime(
        route1,
        originStop.id,
        transfer.stopId
      );

      if (route1TravelTime === Infinity) continue;

      // Calculate when route1 arrives at transfer stop
      const route1ArrivalDate = new Date(route1Arrival);
      const transferArrivalTime = new Date(
        route1ArrivalDate.getTime() + route1TravelTime * 60 * 1000
      );

      // Calculate when route2 departs from transfer stop
      const route2Departure = calculateRouteArrivalTime(
        route2,
        transfer.stopId,
        transferArrivalTime.toISOString(),
        stops
      );

      if (!route2Departure) {
        // Transfer route not available at transfer stop
        continue;
      }

      const route2DepartureDate = new Date(route2Departure);
      if (isNaN(route2DepartureDate.getTime())) {
        console.warn(`FRODO: Invalid route2 departure time for transfer at stop ${transfer.stopId}`);
        continue;
      }
      
      const transferWaitTimeMinutes =
        (route2DepartureDate.getTime() - transferArrivalTime.getTime()) / (1000 * 60);

      // Check transfer wait time constraint
      if (transferWaitTimeMinutes < 0 || transferWaitTimeMinutes > config.maxWaitTimeMinutes) {
        continue; // Transfer wait time exceeds constraint
      }
      
      // Validate transfer stop exists on both routes
      const transferStopOnRoute1 = route1.stops.some((s) => s.stopId === transfer.stopId);
      const transferStopOnRoute2 = route2.stops.some((s) => s.stopId === transfer.stopId);
      
      if (!transferStopOnRoute1 || !transferStopOnRoute2) {
        console.warn(`FRODO: Transfer stop ${transfer.stopId} not found on both routes`);
        continue;
      }

      // Calculate travel time from transfer stop to destination stop on route2
      const route2TravelTime = calculateRouteTravelTime(
        route2,
        transfer.stopId,
        destStop.id
      );

      if (route2TravelTime === Infinity) continue;

      // Calculate initial wait time at origin stop
      const requestDate = new Date(ride.request_time);
      const waitTimeMinutes = (route1ArrivalDate.getTime() - requestDate.getTime()) / (1000 * 60);

      if (waitTimeMinutes < 0 || waitTimeMinutes > config.maxWaitTimeMinutes) {
        continue;
      }

      const totalRouteTravelTime = route1TravelTime + transferWaitTimeMinutes + route2TravelTime;
      const totalWalkingDistance = originDistance + destDistance;

      // Score: prioritize walking distance, then route time, then wait time
      const score =
        totalWalkingDistance * 1000 + // Walking distance (primary)
        totalRouteTravelTime * 10 + // Total route time (secondary)
        waitTimeMinutes + // Initial wait time (tertiary)
        transferWaitTimeMinutes * 0.5; // Transfer wait (less important)

      const transferStop = stops.get(transfer.stopId);

      const evaluation: StopPairEvaluation = {
        routeId: route1.id,
        routeName: route1.name,
        transferRouteId: route2.id,
        transferRouteName: route2.name,
        transferStopId: transfer.stopId,
        transferStopName: transferStop?.name || transfer.stopId,
        originStop,
        destStop,
        originDistance,
        destDistance,
        waitTimeMinutes,
        transferWaitTimeMinutes,
        routeArrivalTime: route1Arrival,
        routeTravelTimeMinutes: totalRouteTravelTime,
        totalWalkingDistance,
        requiresTransfer: true,
        score,
      };

      // Keep best transfer option
      if (!bestEvaluation || evaluation.score < bestEvaluation.score) {
        bestEvaluation = evaluation;
      }
    }
  }

  return bestEvaluation;
}

/**
 * Find the best stop pair from all candidate stops
 * 
 * @param ride The ride to find best stop pair for
 * @param candidateOriginStops Candidate origin stops with distances
 * @param candidateDestStops Candidate destination stops with distances
 * @param selectedRoutes Routes to consider
 * @param config FRODO configuration
 * @param stops Map of all stops
 * @returns Best stop pair evaluation, or null if no valid match found
 */
export function findBestStopPair(
  ride: RideSchema,
  candidateOriginStops: CandidateStop[],
  candidateDestStops: CandidateStop[],
  selectedRoutes: FixedRoute[],
  config: FrodoConfig,
  stops: Map<string, FixedRouteStop>
): StopPairEvaluation | null {
  if (candidateOriginStops.length === 0 || candidateDestStops.length === 0) {
    return null;
  }

  // Limit candidates if too many to prevent combinatorial explosion
  const MAX_CANDIDATES = 15;
  const limitedOriginStops =
    candidateOriginStops.length > MAX_CANDIDATES
      ? candidateOriginStops.slice(0, MAX_CANDIDATES)
      : candidateOriginStops;
  const limitedDestStops =
    candidateDestStops.length > MAX_CANDIDATES
      ? candidateDestStops.slice(0, MAX_CANDIDATES)
      : candidateDestStops;

  if (candidateOriginStops.length > MAX_CANDIDATES || candidateDestStops.length > MAX_CANDIDATES) {
    console.warn(
      `FRODO: Limiting candidate stops to prevent combinatorial explosion. Origin: ${candidateOriginStops.length} -> ${limitedOriginStops.length}, Dest: ${candidateDestStops.length} -> ${limitedDestStops.length}`
    );
  }

  let bestEvaluation: StopPairEvaluation | null = null;
  let bestScore = Infinity;

  // Evaluate all combinations
  for (const originCandidate of limitedOriginStops) {
    for (const destCandidate of limitedDestStops) {
      const evaluation = evaluateStopPair(
        ride,
        originCandidate.stop,
        destCandidate.stop,
        originCandidate.distance,
        destCandidate.distance,
        selectedRoutes,
        config,
        stops
      );

      if (evaluation && evaluation.score < bestScore) {
        bestEvaluation = evaluation;
        bestScore = evaluation.score;

        // Early exit optimization: if we found a perfect match (very low score)
        if (evaluation.score < 100) {
          return bestEvaluation;
        }
      }
    }
  }

  return bestEvaluation;
}

/**
 * Check if a ride is eligible for fixed route service
 * 
 * @param ride The ride to check
 * @param spatialIndex Spatial index of stops
 * @param selectedRoutes Routes to consider
 * @param config FRODO configuration
 * @param stops Map of all stops
 * @returns Eligibility result with metadata, or null if not eligible
 */
export function isRideEligibleForFixedRoute(
  ride: RideSchema,
  spatialIndex: SpatialIndex,
  selectedRoutes: FixedRoute[],
  config: FrodoConfig,
  stops: Map<string, FixedRouteStop>
): FrodoOffloadedRideMetadata | null {
  // Check wheelchair filter
  if (config.excludeWheelchairUsers) {
    const hasWheelchair = ride.passengers?.some(
      (p) => p.type?.toLowerCase().includes('wheelchair') || p.type?.toLowerCase().includes('wchair')
    );
    if (hasWheelchair) {
      return null; // Exclude wheelchair users
    }
  }

  // Find candidate stops
  const { originStops, destStops } = findCandidateStops(ride, spatialIndex, config.maxDistanceToStop);

  if (originStops.length === 0 || destStops.length === 0) {
    return null; // No candidate stops found
  }

  // Find best stop pair
  const bestEvaluation = findBestStopPair(
    ride,
    originStops,
    destStops,
    selectedRoutes,
    config,
    stops
  );

  if (!bestEvaluation) {
    return null; // No valid route found
  }

  // Verify wait time constraints (already checked in evaluateStopPair, but double-check)
  if (bestEvaluation.waitTimeMinutes < 0 || bestEvaluation.waitTimeMinutes > config.maxWaitTimeMinutes) {
    return null;
  }

  // Build metadata
  const metadata: FrodoOffloadedRideMetadata = {
    routeId: bestEvaluation.routeId,
    routeName: bestEvaluation.routeName,
    originStopId: bestEvaluation.originStop.id,
    originStopName: bestEvaluation.originStop.name,
    originDistance: bestEvaluation.originDistance,
    destStopId: bestEvaluation.destStop.id,
    destStopName: bestEvaluation.destStop.name,
    destDistance: bestEvaluation.destDistance,
    waitTimeMinutes: bestEvaluation.waitTimeMinutes,
    routeArrivalTime: bestEvaluation.routeArrivalTime,
    requiresTransfer: bestEvaluation.requiresTransfer,
  };

  if (bestEvaluation.requiresTransfer) {
    metadata.transferRouteId = bestEvaluation.transferRouteId;
    metadata.transferRouteName = bestEvaluation.transferRouteName;
    metadata.transferStopId = bestEvaluation.transferStopId;
    metadata.transferStopName = bestEvaluation.transferStopName;
    metadata.transferWaitTimeMinutes = bestEvaluation.transferWaitTimeMinutes;
  }

  return metadata;
}

/**
 * Process FRODO optimization on a dataset of rides
 * 
 * @param rides Array of rides to process
 * @param selectedRoutes Routes to consider for optimization
 * @param stops Array of all stops
 * @param config FRODO configuration
 * @returns Object with offloaded rides and remaining rides
 */
export function processFrodoOptimization(
  rides: RideSchema[],
  selectedRoutes: FixedRoute[],
  stops: FixedRouteStop[],
  config: FrodoConfig
): {
  offloadedRides: FrodoOffloadedRide[];
  remainingRides: RideSchema[];
} {
  const startTime = Date.now();

  // Validate inputs
  if (!rides || rides.length === 0) {
    console.warn('FRODO: No rides provided, returning empty result');
    return {
      offloadedRides: [],
      remainingRides: [],
    };
  }

  if (!selectedRoutes || selectedRoutes.length === 0) {
    console.warn('FRODO: No routes selected, returning all rides as remaining');
    return {
      offloadedRides: [],
      remainingRides: rides,
    };
  }

  if (!stops || stops.length === 0) {
    console.warn('FRODO: No stops provided, returning all rides as remaining');
    return {
      offloadedRides: [],
      remainingRides: rides,
    };
  }

  // Validate config
  if (config.maxDistanceToStop <= 0) {
    console.error('FRODO: Invalid maxDistanceToStop, must be > 0');
    return {
      offloadedRides: [],
      remainingRides: rides,
    };
  }

  if (config.maxWaitTimeMinutes <= 0) {
    console.error('FRODO: Invalid maxWaitTimeMinutes, must be > 0');
    return {
      offloadedRides: [],
      remainingRides: rides,
    };
  }

  try {
    // Filter to only include routes that exist
    const validRoutes = selectedRoutes.filter((route) => route.id && route.stops.length > 0);

    if (validRoutes.length === 0) {
      console.warn('FRODO: No valid routes selected, returning all rides as remaining');
      return {
        offloadedRides: [],
        remainingRides: rides,
      };
    }

  // Filter stops to only include stops from selected routes
  const routeStopIds = new Set<string>();
  validRoutes.forEach((route) => {
    route.stops.forEach((stop) => routeStopIds.add(stop.stopId));
  });

  const filteredStops = stops.filter((stop) => routeStopIds.has(stop.id));

  if (filteredStops.length === 0) {
    console.warn('FRODO: No stops found for selected routes, returning all rides as remaining');
    return {
      offloadedRides: [],
      remainingRides: rides,
    };
  }

  // Build spatial index once and reuse for all rides
  const spatialIndex = new SpatialIndex();
  spatialIndex.buildIndex(filteredStops);

  // Create stops map for quick lookup
  const stopsMap = new Map<string, FixedRouteStop>();
  filteredStops.forEach((stop) => {
    stopsMap.set(stop.id, stop);
  });

  const offloadedRides: FrodoOffloadedRide[] = [];
  const remainingRides: RideSchema[] = [];

  let spatialQueries = 0;
  let stopPairEvaluations = 0;
  let candidateLimitingCases = 0;

  // Process each ride
  for (const ride of rides) {
    try {
      // Validate ride has required coordinates
      if (
        !Number.isFinite(ride.from_latitude) ||
        !Number.isFinite(ride.from_longitude) ||
        !Number.isFinite(ride.to_latitude) ||
        !Number.isFinite(ride.to_longitude)
      ) {
        console.warn(`FRODO: Skipping ride with invalid coordinates: ${ride.ride_id || 'unknown'}`);
        remainingRides.push(ride);
        continue;
      }

      // Validate ride has request time
      if (!ride.request_time) {
        console.warn(`FRODO: Skipping ride without request_time: ${ride.ride_id || 'unknown'}`);
        remainingRides.push(ride);
        continue;
      }

      spatialQueries += 2; // One for origin, one for destination

      const metadata = isRideEligibleForFixedRoute(
        ride,
        spatialIndex,
        validRoutes,
        config,
        stopsMap
      );

      if (metadata) {
        // Count evaluations (approximate - based on candidate stops)
        const { originStops, destStops } = findCandidateStops(
          ride,
          spatialIndex,
          config.maxDistanceToStop
        );
        const evaluations = Math.min(originStops.length, 15) * Math.min(destStops.length, 15);
        stopPairEvaluations += evaluations;

        if (originStops.length > 15 || destStops.length > 15) {
          candidateLimitingCases++;
        }

        offloadedRides.push({
          ...ride,
          frodoMetadata: metadata,
        });
      } else {
        // Log why ride wasn't matched (for debugging)
        if (rides.length <= 10) { // Only log for small batches to avoid spam
          const { originStops, destStops } = findCandidateStops(
            ride,
            spatialIndex,
            config.maxDistanceToStop
          );
          console.debug(`FRODO: Ride ${ride.ride_id || 'unknown'} not matched - origin stops: ${originStops.length}, dest stops: ${destStops.length}, request time: ${ride.request_time}`);
        }
        remainingRides.push(ride);
      }
    } catch (error) {
      console.error(`FRODO: Error processing ride ${ride.ride_id || 'unknown'}:`, error);
      remainingRides.push(ride); // Fail-safe: keep ride in on-demand
    }
  }

  const processingTime = Date.now() - startTime;

  // Performance logging
  console.log(`FRODO: Preprocessing complete in ${processingTime}ms`);
  console.log(`FRODO: Offloaded ${offloadedRides.length} rides, ${remainingRides.length} remaining`);
  console.log(`FRODO: Spatial queries: ${spatialQueries} (${spatialQueries / rides.length} per ride)`);
  console.log(`FRODO: Stop pair evaluations: ${stopPairEvaluations} (avg ${(stopPairEvaluations / rides.length).toFixed(1)} per ride)`);
  if (candidateLimitingCases > 0) {
    console.log(`FRODO: Candidate limiting applied to ${candidateLimitingCases} rides`);
  }

    return {
      offloadedRides,
      remainingRides,
    };
  } catch (error) {
    console.error('FRODO: Critical error in processFrodoOptimization:', error);
    // Fail-safe: return all rides as remaining if critical error occurs
    return {
      offloadedRides: [],
      remainingRides: rides,
    };
  }
}

/**
 * Generate FRODO report from offloaded rides
 * 
 * @param offloadedRides Rides that were offloaded to fixed routes
 * @param totalRides Total number of rides in original dataset
 * @param stops Map of all stops (for stop names)
 * @returns FRODO report with statistics
 */
export function generateFrodoReport(
  offloadedRides: FrodoOffloadedRide[],
  totalRides: number,
  stops: Map<string, FixedRouteStop>
): FrodoReport {
  try {
    if (!offloadedRides || offloadedRides.length === 0) {
      return {
        totalOffloaded: 0,
        totalRemaining: totalRides || 0,
        offloadPercentage: 0,
        routeStats: [],
      };
    }

    const totalOffloaded = offloadedRides.length;
    const totalRemaining = (totalRides || 0) - totalOffloaded;
    const offloadPercentage = totalRides > 0 ? (totalOffloaded / totalRides) * 100 : 0;

  // Group offloaded rides by routeId
  const ridesByRoute = new Map<string, FrodoOffloadedRide[]>();
  offloadedRides.forEach((ride) => {
    const routeId = ride.frodoMetadata.routeId;
    if (!ridesByRoute.has(routeId)) {
      ridesByRoute.set(routeId, []);
    }
    ridesByRoute.get(routeId)!.push(ride);
  });

  // Calculate statistics for each route
  const routeStats: FrodoRouteStats[] = [];

  ridesByRoute.forEach((rides, routeId) => {
    const routeName = rides[0].frodoMetadata.routeName;
    const rideCount = rides.length;

    // Calculate averages
    const avgWaitTime =
      rides.reduce((sum, ride) => sum + ride.frodoMetadata.waitTimeMinutes, 0) / rideCount;
    const avgOriginDistance =
      rides.reduce((sum, ride) => sum + ride.frodoMetadata.originDistance, 0) / rideCount;
    const avgDestDistance =
      rides.reduce((sum, ride) => sum + ride.frodoMetadata.destDistance, 0) / rideCount;

    // Ride type breakdown
    const departureFixed = rides.filter(
      (ride) => ride.request_type === 'departure_fixed'
    ).length;
    const arrivalFixed = rides.filter((ride) => ride.request_type === 'arrival_fixed').length;

    // Transfer statistics
    const transferRides = rides.filter((ride) => ride.frodoMetadata.requiresTransfer);
    let transferStats: FrodoTransferStats | undefined;

    if (transferRides.length > 0) {
      const avgTransferWaitTime =
        transferRides.reduce(
          (sum, ride) => sum + (ride.frodoMetadata.transferWaitTimeMinutes || 0),
          0
        ) / transferRides.length;

      // Count transfer route combinations
      const transferRouteMap = new Map<string, number>();
      transferRides.forEach((ride) => {
        if (ride.frodoMetadata.transferRouteId) {
          const key = `${ride.frodoMetadata.routeId}-${ride.frodoMetadata.transferRouteId}`;
          transferRouteMap.set(key, (transferRouteMap.get(key) || 0) + 1);
        }
      });

      const transferRoutes = Array.from(transferRouteMap.entries()).map(([key, count]) => {
        const [route1Id, route2Id] = key.split('-');
        const route1 = rides.find((r) => r.frodoMetadata.routeId === route1Id);
        const route2 = rides.find((r) => r.frodoMetadata.transferRouteId === route2Id);
        return {
          routeId: route2Id,
          routeName: route2?.frodoMetadata.transferRouteName || route2Id,
          count,
        };
      });

      transferStats = {
        transferRideCount: transferRides.length,
        avgTransferWaitTime,
        transferRoutes,
      };
    }

    // Timing statistics
    const requestTimes = rides
      .map((ride) => ride.request_time)
      .filter((time): time is string => !!time)
      .sort();

    const earliestRequest = requestTimes[0] || '';
    const latestRequest = requestTimes[requestTimes.length - 1] || '';

    // Wait time distribution (0-5, 5-10, 10-15, 15-20, 20+ minutes)
    const waitTimeRanges = [
      { range: '0-5 min', min: 0, max: 5 },
      { range: '5-10 min', min: 5, max: 10 },
      { range: '10-15 min', min: 10, max: 15 },
      { range: '15-20 min', min: 15, max: 20 },
      { range: '20+ min', min: 20, max: Infinity },
    ];

    const waitTimeDistribution = waitTimeRanges.map((range) => ({
      range: range.range,
      count: rides.filter(
        (ride) =>
          ride.frodoMetadata.waitTimeMinutes >= range.min &&
          ride.frodoMetadata.waitTimeMinutes < range.max
      ).length,
    }));

    const timingStats: FrodoTimingStats = {
      earliestRequest,
      latestRequest,
      waitTimeDistribution,
    };

    routeStats.push({
      routeId,
      routeName,
      rideCount,
      avgWaitTime,
      avgOriginDistance,
      avgDestDistance,
      rideTypeBreakdown: {
        departure_fixed: departureFixed,
        arrival_fixed: arrivalFixed,
      },
      transferStats,
      timingStats,
    });
  });

  // Sort route stats by ride count (descending)
  routeStats.sort((a, b) => b.rideCount - a.rideCount);

    return {
      totalOffloaded,
      totalRemaining,
      offloadPercentage,
      routeStats,
    };
  } catch (error) {
    console.error('FRODO: Error generating report:', error);
    // Return empty report on error
    return {
      totalOffloaded: 0,
      totalRemaining: totalRides || 0,
      offloadPercentage: 0,
      routeStats: [],
    };
  }
}

